import React, { useEffect, useRef, useState } from "react";
import {
    Alert, Box, Breadcrumbs, Button, CircularProgress, Grid, Link, MenuItem,
    Paper, Snackbar, TextField, Typography, Stack,
    Divider, Card, CardContent, Dialog, DialogTitle, DialogContent,
    DialogContentText, DialogActions, Select, Chip, Tooltip, IconButton
} from "@mui/material";
import {
    NavigateNext, ArrowBack, Save, Clear, Person, FamilyRestroom,
    PersonAddAlt1, PersonRemove
} from '@mui/icons-material';
import { FieldArray, Form, Formik } from "formik";
import * as yup from "yup";
import Header from "../../component/header/Header";
import { useNavigate } from "react-router-dom";

// --- UI Standard Colors (Kept exactly as original) ---
const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    accent: '#FFC107',
    accentDark: '#FFA000',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037',
    white: '#FFFFFF',
    filterBg: "#FFF9E6",
    orange: { 400: '#FFC107' }
};

const AddParentScreen = () => {
    const navigation = useNavigate();
    const formikRef = useRef();

    // --- State Management ---
    const [courseList, setCourseList] = useState([]);
    const [classList, setClassList] = useState([]);
    const [openSummary, setOpenSummary] = useState(false);
    const [successCreateParent, setSuccessCreateParent] = useState(false);
    const [failCreateParent, setFailCreateParent] = useState(false);
    const [failErrorMsg, setFailErrorMsg] = useState("");
    const [loadingSubmit, setLoadingSubmit] = useState(false);

    useEffect(() => {
        getCourseList();
        getBatchList();
        localStorage.setItem("current_tab", "Parent");
    }, []);

    // --- API Calls ---
    const getCourseList = () => {
        fetch(`https://www.geniusmileniaeducation.link/db-api/courses`)
            .then((response) => response.json())
            .then((responseJson) => setCourseList(responseJson.data || []))
            .catch((error) => console.log(error));
    };

    const getBatchList = () => {
        fetch(`https://www.geniusmileniaeducation.link/db-api/batch`)
            .then((response) => response.json())
            .then((responseJson) => setClassList(responseJson.data || []))
            .catch((error) => console.log(error));
    };

    // --- Logic Fix: Combined Create Logic ---
    const handleRegisterProcess = async (values) => {
        setLoadingSubmit(true);
        try {
            // 1. Create Parent
            const parentRes = await fetch(`https://www.geniusmileniaeducation.link/db-api/parents`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fullname: values.fullname,
                    mobileNo: values.mobileNo,
                    password: "123456",
                    status: values.status,
                    location: values.location,
                    pushNotiToken: "",
                    device: "",
                    point: values.point,
                    registerDate: values.registerDate
                })
            });
            const parentData = await parentRes.json();

            if (parentData.status === 1) {
                // 2. Create Students using the parent ID
                const studentRes = await fetch(`https://www.geniusmileniaeducation.link/db-api/students-course/bulk/${parentData.message}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(values.studentIds)
                });
                const studentData = await studentRes.json();

                if (studentData.status === 1) {
                    setOpenSummary(false);
                    setSuccessCreateParent(true);
                    formikRef.current?.resetForm();
                } else {
                    throw new Error(studentData.message || "Failed to add students");
                }
            } else {
                throw new Error(parentData.message || "Failed to add parent");
            }
        } catch (error) {
            setFailCreateParent(true);
            setFailErrorMsg(error.message);
        } finally {
            setLoadingSubmit(false);
        }
    };

    // --- Components for Inputs (Original Style) ---
    const InputTextFull = ({ title, name, value, onChange, onBlur, error, helperText, mandatory, ...props }) => (
        <Grid item xs={12}>
            <Typography variant="body2" fontWeight="bold" mb={1}>{title} {mandatory && <span style={{ color: "red" }}>*</span>}</Typography>
            <TextField fullWidth size="small" name={name} value={value} onChange={onChange} onBlur={onBlur} error={error} helperText={helperText} {...props} sx={{ bgcolor: 'white' }} />
        </Grid>
    );

    const InputText = ({ title, name, value, onChange, mandatory, ...props }) => (
        <Grid item xs={12} md={6}>
            <Typography variant="body2" fontWeight="bold" mb={1}>{title} {mandatory && <span style={{ color: "red" }}>*</span>}</Typography>
            <TextField fullWidth size="small" name={name} value={value} onChange={onChange} {...props} sx={{ bgcolor: 'white' }} />
        </Grid>
    );

    const InputSelect = ({ title, name, value, onChange, mandatory, children }) => (
        <Grid item xs={12} md={6}>
            <Typography variant="body2" fontWeight="bold" mb={1}>{title} {mandatory && <span style={{ color: "red" }}>*</span>}</Typography>
            <TextField select fullWidth size="small" name={name} value={value} onChange={onChange} sx={{ bgcolor: 'white' }}>
                {children}
            </TextField>
        </Grid>
    );

    const InputDate = ({ title, name, value, onChange, mandatory }) => (
        <Grid item xs={12} md={6}>
            <Typography variant="body2" fontWeight="bold" mb={1}>{title} {mandatory && <span style={{ color: "red" }}>*</span>}</Typography>
            <TextField type="date" fullWidth size="small" name={name} value={value} onChange={onChange} InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white' }} />
        </Grid>
    );

    const initialValues = {
        fullname: "", mobileNo: "", email: "", location: "PJ", status: "A", point: "0", studentIds: [], registerDate: new Date().toISOString().split('T')[0]
    };

    // --- Robust Validation Schema ---
    const validationSchema = yup.object({
        fullname: yup.string().required("Parent Name is required"),
        mobileNo: yup.string().required("Mobile number is required"),
        email: yup.string().email("Invalid email").required("Email is required"),
        studentIds: yup.array().of(
            yup.object({
                fullname: yup.string().required("Required"),
                gender: yup.string().required("Required"),
                class: yup.string().required("Required"),
                amount: yup.number().required("Required"),
                course: yup.array().min(1, "Select at least 1 course")
            })
        ).min(1, "Add at least one student")
    });

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>

                <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")}>Portal</Link>
                    <Link underline="hover" color="inherit" onClick={() => navigation("/Portal/Parent")}>Parents</Link>
                    <Typography color="text.primary">Register</Typography>
                </Breadcrumbs>

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Header title="Register Parent" subtitle="Add new parent and enroll students" />
                    <Button startIcon={<ArrowBack />} onClick={() => navigation("/Portal/Parent")} color="inherit">Back to List</Button>
                </Stack>

                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={() => setOpenSummary(true)}
                >
                    {({ values, errors, touched, handleBlur, handleChange, setFieldValue }) => (
                        <Form>
                            <Grid container spacing={3}>
                                {/* SECTION 1: PARENT INFORMATION */}
                                <Grid item xs={12} md={4}>
                                    <Paper elevation={0} sx={{ p: 3, bgcolor: colors.filterBg, borderRadius: 3, border: '1px solid #F0E6CC', height: '100%' }}>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                                            <Person sx={{ color: colors.textMain }} />
                                            <Typography variant="h6" fontWeight="bold">Parent Information</Typography>
                                        </Stack>
                                        <Grid container spacing={2}>
                                            <InputTextFull title="Parent Name" name="fullname" value={values.fullname} onChange={handleChange} onBlur={handleBlur} mandatory error={touched.fullname && !!errors.fullname} helperText={touched.fullname && errors.fullname} />
                                            <InputTextFull title="Mobile Number" name="mobileNo" value={values.mobileNo} onChange={handleChange} onBlur={handleBlur} mandatory error={touched.mobileNo && !!errors.mobileNo} helperText={touched.mobileNo && errors.mobileNo} />
                                            <InputTextFull title="Email" name="email" value={values.email} onChange={handleChange} onBlur={handleBlur} mandatory error={touched.email && !!errors.email} helperText={touched.email && errors.email} />
                                            <InputSelect title="Location" name={`location`} value={values.location} onChange={handleChange} mandatory>
                                                <MenuItem value="PJ">PJCC</MenuItem>
                                                <MenuItem value="PD">Pantai Dalam</MenuItem>
                                            </InputSelect>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* SECTION 2: STUDENTS */}
                                <Grid item xs={12} md={8}>
                                    <FieldArray name="studentIds">
                                        {arrayHelpers => (
                                            <Paper elevation={0} sx={{ p: 3, border: '1px solid #F0E6CC', borderRadius: 3 }}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <FamilyRestroom sx={{ color: colors.accent }} />
                                                        <Typography variant="h6" fontWeight="bold">Student(s) Information</Typography>
                                                    </Stack>
                                                    <Button variant="contained" startIcon={<PersonAddAlt1 />} onClick={() => arrayHelpers.push({ fullname: "", gender: "", dateOfBirth: "", class: "", amount: "", course: [], status: "A" })} sx={{ bgcolor: colors.accent, color: 'black', '&:hover': { bgcolor: colors.accentDark } }}>Add Student</Button>
                                                </Stack>

                                                {values.studentIds.map((student, index) => (
                                                    <Card key={index} elevation={0} sx={{ mb: 2, border: '1px solid #E0E0E0', borderRadius: 2 }}>
                                                        <Box sx={{ p: 1.5, bgcolor: colors.tableHeader, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography fontWeight="bold" sx={{ ml: 1 }}>Student #{index + 1}</Typography>
                                                            <IconButton size="small" onClick={() => arrayHelpers.remove(index)} color="error"><PersonRemove /></IconButton>
                                                        </Box>
                                                        <CardContent>
                                                            <Grid container spacing={2}>
                                                                <InputTextFull title="Student Name" name={`studentIds.${index}.fullname`} value={student.fullname} onChange={handleChange} mandatory />
                                                                <InputSelect title="Gender" name={`studentIds.${index}.gender`} value={student.gender} onChange={handleChange} mandatory>
                                                                    <MenuItem value="Lelaki">Lelaki</MenuItem>
                                                                    <MenuItem value="Perempuan">Perempuan</MenuItem>
                                                                </InputSelect>
                                                                <InputDate title="Date of Birth" name={`studentIds.${index}.dateOfBirth`} value={student.dateOfBirth} onChange={handleChange} mandatory />
                                                                <InputSelect title="Class / Batch" name={`studentIds.${index}.class`} value={student.class} onChange={(e) => { handleChange(e); setFieldValue(`studentIds.${index}.course`, []); }} mandatory>
                                                                    {classList.filter(val => val.status === "A").map((val, i) => (
                                                                        <MenuItem key={i} value={val.batchId}>{val.batchName}</MenuItem>
                                                                    ))}
                                                                </InputSelect>
                                                                <InputText title="Package Amount (RM)" name={`studentIds.${index}.amount`} value={student.amount} onChange={handleChange} mandatory />
                                                                <Grid item xs={12}>
                                                                    <Typography variant="body2" fontWeight="bold" mb={1}>Courses</Typography>
                                                                    <Select multiple fullWidth size="small" name={`studentIds.${index}.course`} value={student.course || []} onChange={handleChange} renderValue={(selected) => (
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                            {selected.map((val) => (<Chip key={val} label={courseList.find(c => c.courseId === val)?.courseName} size="small" />))}
                                                                        </Box>
                                                                    )}>
                                                                        {courseList.filter(c => c.class === classList.find(b => b.batchId === student.class)?.batchName).map((val, i) => (
                                                                            <MenuItem key={i} value={val.courseId}>{val.courseName}</MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </Grid>
                                                            </Grid>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </Paper>
                                        )}
                                    </FieldArray>
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                                        <Button variant="outlined" onClick={() => navigation("/Portal/Parent")} startIcon={<Clear />}>Discard</Button>
                                        <Button type="submit" variant="contained" disabled={loadingSubmit} startIcon={loadingSubmit ? <CircularProgress size={20} color="inherit" /> : <Save />} sx={{ bgcolor: colors.accent, color: 'black', px: 6, '&:hover': { bgcolor: colors.accentDark } }}>
                                            {loadingSubmit ? "Processing..." : "Submit Registration"}
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>

                            {/* Summary Dialog */}
                            <Dialog fullWidth maxWidth="md" open={openSummary} onClose={() => setOpenSummary(false)}>
                                <DialogTitle fontWeight="bold">Confirm Registration Details</DialogTitle>
                                <DialogContent dividers>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}><DialogContentText variant="caption">Parent Name</DialogContentText><Typography fontWeight="bold">{values.fullname}</Typography></Grid>
                                        <Grid item xs={12} sm={4}><DialogContentText variant="caption">Mobile Number</DialogContentText><Typography fontWeight="bold">{values.mobileNo}</Typography></Grid>
                                        <Grid item xs={12}><Divider sx={{ my: 1 }} /><Typography variant="subtitle2">Students ({values.studentIds.length})</Typography>
                                            {values.studentIds.map((s, i) => (<Box key={i} p={1} mb={1} bgcolor="#f5f5f5" borderRadius={1}><Typography variant="body2">{i + 1}. {s.fullname} (RM {s.amount})</Typography></Box>))}
                                        </Grid>
                                    </Grid>
                                </DialogContent>
                                <DialogActions sx={{ p: 2 }}>
                                    <Button onClick={() => setOpenSummary(false)} color="inherit">Edit</Button>
                                    <Button onClick={() => handleRegisterProcess(values)} variant="contained" disabled={loadingSubmit} sx={{ bgcolor: colors.accent, color: 'black' }}>Confirm</Button>
                                </DialogActions>
                            </Dialog>
                        </Form>
                    )}
                </Formik>

                <Snackbar open={successCreateParent} autoHideDuration={4000} onClose={() => setSuccessCreateParent(false)}>
                    <Alert severity="success" variant="filled">Parent created successfully</Alert>
                </Snackbar>
                <Snackbar open={failCreateParent} autoHideDuration={4000} onClose={() => setFailCreateParent(false)}>
                    <Alert severity="error" variant="filled">Error: {failErrorMsg}</Alert>
                </Snackbar>
            </Box>
        </Box>
    );
};

export default AddParentScreen;